import React from "react";
import { formatINR, formatDate } from "./subscriptionUtils";

const COLORS = {
    primary: "#2563eb",
    primaryDark: "#1e3a8a",
    ink: "#0f172a",
    sub: "#64748b",
    line: "#e2e8f0",
    soft: "#f8fafc",
};

const cell = (extra = {}) => ({ verticalAlign: "top", ...extra });

const LogoBox = ({ src, size = 64, alt = "logo" }) => (
    <div
        style={{
            width: size,
            height: size,
            margin: "0 auto",
            background: "#ffffff",
            borderRadius: 12,
            border: `1px solid ${COLORS.line}`,
            display: "table",
        }}
    >
        <div style={{ display: "table-cell", verticalAlign: "middle", textAlign: "center", padding: 6 }}>
            {src ? (
                <img
                    src={src}
                    alt={alt}
                    style={{ maxWidth: size - 16, maxHeight: size - 16, objectFit: "contain", display: "inline-block", verticalAlign: "middle" }}
                />
            ) : (
                <span style={{ fontSize: 11, color: COLORS.sub }}>—</span>
            )}
        </div>
    </div>
);

const InvoiceDocument = ({ invoice, companyLogo, clientLogo }) => {
    if (!invoice) return null;

    const company = invoice.company || {};
    const client = invoice.client || {};
    const bank = company.bankDetails || {};
    const isPaid = invoice.status === "paid";

    const fromLines = [
        company.name || company.legalName,
        company.legalName && company.legalName !== company.name ? company.legalName : null,
        company.addressLine1,
        company.addressLine2,
        [company.city, company.state, company.pincode].filter(Boolean).join(", ") || null,
        company.gstin ? `GSTIN: ${company.gstin}` : null,
        [company.email, company.phone].filter(Boolean).join("  •  ") || null,
        company.website,
    ].filter(Boolean);

    const billToLines = [
        client.branchName,
        client.ownerName,
        client.businessCategory,
        [client.state, client.pincode].filter(Boolean).join(", ") || null,
        client.gstin ? `GSTIN: ${client.gstin}` : null,
        [client.email, client.mobile].filter(Boolean).join("  •  ") || null,
    ].filter(Boolean);

    return (
        <div
            id="subscription-invoice-document"
            style={{
                width: 794,
                background: "#ffffff",
                color: COLORS.ink,
                fontFamily: "Helvetica, Arial, sans-serif",
                boxSizing: "border-box",
                fontSize: 13,
            }}
        >
            {/* Header — table layout for reliable PDF capture */}
            <table style={{ width: "100%", borderCollapse: "collapse", background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 100%)` }}>
                <tbody>
                    <tr>
                        <td style={{ ...cell(), padding: "28px 24px 28px 40px", width: "58%" }}>
                            <table style={{ borderCollapse: "collapse" }}>
                                <tbody>
                                    <tr>
                                        <td style={{ verticalAlign: "middle", paddingRight: 16 }}>
                                            {companyLogo ? (
                                                <div style={{ width: 72, height: 72, background: "#fff", borderRadius: 12, display: "table" }}>
                                                    <div style={{ display: "table-cell", verticalAlign: "middle", textAlign: "center" }}>
                                                        <img src={companyLogo} alt="Company" style={{ maxWidth: 60, maxHeight: 60, objectFit: "contain", paddingLeft: 13 }} />
                                                    </div>
                                                </div>
                                            ) : (
                                                <div style={{ width: 72, height: 72, borderRadius: 12, background: "rgba(255,255,255,0.2)", color: "#fff", display: "table" }}>
                                                    <div style={{ display: "table-cell", verticalAlign: "middle", textAlign: "center", fontSize: 28, fontWeight: 700 }}>
                                                        {(company.name || "Z").charAt(0)}
                                                    </div>
                                                </div>
                                            )}
                                        </td>
                                        <td style={{ verticalAlign: "middle", color: "#fff" }}>
                                            <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.2 }}>{company.name || "Company"}</div>
                                            {company.legalName && company.legalName !== company.name && (
                                                <div style={{ fontSize: 11, opacity: 0.9, marginTop: 4 }}>{company.legalName}</div>
                                            )}
                                            {company.website && <div style={{ fontSize: 11, opacity: 0.85, marginTop: 4 }}>{company.website}</div>}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </td>
                        <td style={{ ...cell(), padding: "28px 40px 28px 16px", textAlign: "center", width: "42%" }}>
                            <div style={{ color: "#fff", fontSize: 28, fontWeight: 800, letterSpacing: 2, textAlign: "center" }}>INVOICE</div>
                            <div style={{ color: "#fff", fontSize: 12, opacity: 0.92, marginTop: 6, textAlign: "center" }}>{invoice.invoiceNumber || "—"}</div>
                        </td>
                    </tr>
                </tbody>
            </table>

            {/* Meta strip */}
            <table style={{ width: "100%", borderCollapse: "collapse", background: COLORS.soft, borderBottom: `1px solid ${COLORS.line}` }}>
                <tbody>
                    <tr>
                        <MetaCell label="Issue Date" value={formatDate(invoice.issueDate)} />
                        <MetaCell label="Due Date" value={formatDate(invoice.dueDate)} />
                        <MetaCell
                            label="Billing Period"
                            value={invoice.billingPeriodStart ? `${formatDate(invoice.billingPeriodStart)} – ${formatDate(invoice.billingPeriodEnd)}` : "—"}
                        />
                        <MetaCell label="Plan" value={invoice.planName || "—"} last />
                    </tr>
                </tbody>
            </table>

            {/* From / Bill To */}
            <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 8 }}>
                <tbody>
                    <tr>
                        <td style={{ ...cell(), width: "50%", padding: "24px 20px 16px 40px" }}>
                            <SectionLabel>From</SectionLabel>
                            <div style={{ marginTop: 10 }}>
                                {fromLines.length > 0 ? (
                                    fromLines.map((line, i) => (
                                        <div
                                            key={i}
                                            style={{
                                                fontSize: i === 0 ? 14 : 12,
                                                fontWeight: i === 0 ? 700 : 400,
                                                color: i === 0 ? COLORS.ink : COLORS.sub,
                                                marginTop: i === 0 ? 0 : 4,
                                                lineHeight: 1.45,
                                            }}
                                        >
                                            {line}
                                        </div>
                                    ))
                                ) : (
                                    <div style={{ fontSize: 12, color: COLORS.sub }}>Company details not configured</div>
                                )}
                            </div>
                        </td>
                        <td style={{ ...cell(), width: "50%", padding: "24px 40px 16px 20px" }}>
                            <div style={{ background: COLORS.soft, borderRadius: 12, border: `1px solid ${COLORS.line}`, padding: 16 }}>
                                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                    <tbody>
                                        <tr>
                                            <td style={{ verticalAlign: "top", width: "68%" }}>
                                                <SectionLabel>Bill To</SectionLabel>
                                                <div style={{ marginTop: 10 }}>
                                                    {billToLines.length > 0 ? (
                                                        billToLines.map((line, i) => (
                                                            <div
                                                                key={i}
                                                                style={{
                                                                    fontSize: i === 0 ? 14 : 12,
                                                                    fontWeight: i === 0 ? 700 : 400,
                                                                    color: i === 0 ? COLORS.ink : COLORS.sub,
                                                                    marginTop: i === 0 ? 0 : 4,
                                                                    lineHeight: 1.45,
                                                                }}
                                                            >
                                                                {line}
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <div style={{ fontSize: 12, color: COLORS.sub }}>—</div>
                                                    )}
                                                </div>
                                            </td>
                                            <td style={{ verticalAlign: "middle", textAlign: "center", width: 80, paddingLeft: 8 }}>
                                                <LogoBox src={clientLogo} size={64} alt="Client" />
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>

            {/* Line items */}
            <div style={{ padding: "12px 40px 0" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                        <tr style={{ background: COLORS.primary, color: "#fff" }}>
                            <th style={{ textAlign: "left", padding: "11px 14px", fontWeight: 600 }}>Description</th>
                            <th style={{ textAlign: "center", padding: "11px 14px", fontWeight: 600, width: 64 }}>Qty</th>
                            <th style={{ textAlign: "right", padding: "11px 14px", fontWeight: 600, width: 120 }}>Rate</th>
                            <th style={{ textAlign: "right", padding: "11px 14px", fontWeight: 600, width: 130 }}>Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(invoice.lineItems || []).map((it, i) => (
                            <tr key={i} style={{ background: i % 2 ? COLORS.soft : "#fff" }}>
                                <td style={{ padding: "10px 14px", borderBottom: `1px solid ${COLORS.line}` }}>{it.description}</td>
                                <td style={{ padding: "10px 14px", textAlign: "center", color: COLORS.sub, borderBottom: `1px solid ${COLORS.line}` }}>{it.qty || 1}</td>
                                <td style={{ padding: "10px 14px", textAlign: "right", borderBottom: `1px solid ${COLORS.line}` }}>{formatINR(it.unitPrice)}</td>
                                <td style={{ padding: "10px 14px", textAlign: "right", fontWeight: 600, borderBottom: `1px solid ${COLORS.line}` }}>{formatINR(it.amount)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Totals */}
            <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 16 }}>
                <tbody>
                    <tr>
                        <td style={{ width: "55%" }} />
                        <td style={{ width: "45%", padding: "0 40px 0 0", verticalAlign: "top" }}>
                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                <tbody>
                                    <TotalRow label="Subtotal" value={formatINR(invoice.subtotal)} />
                                    {invoice.gstEnabled && <TotalRow label={`GST (${invoice.gstRate || 0}%)`} value={formatINR(invoice.gstAmount)} />}
                                    <tr>
                                        <td colSpan={2} style={{ paddingTop: 8 }}>
                                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                                <tbody>
                                                    <tr>
                                                        <td style={{ padding: "12px 8px", color: COLORS.primary, fontSize: 20, fontWeight: 600 }}>Total Amount</td>
                                                        <td style={{ padding: "12px 8px", color: COLORS.primary, fontSize: 20, fontWeight: 800, textAlign: "right" }}>{formatINR(invoice.total)}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </td>
                    </tr>
                </tbody>
            </table>

            {/* Payment details */}
            {(company.upiId || bank.accountNumber) && (
                <div style={{ padding: "24px 40px 0" }}>
                    <SectionLabel>Payment Details</SectionLabel>
                    <div style={{ marginTop: 8, fontSize: 12, color: COLORS.sub, lineHeight: 1.7 }}>
                        {company.upiId && (
                            <div>
                                UPI: <span style={{ color: COLORS.ink, fontWeight: 600 }}>{company.upiId}</span>
                                {company.upiName ? ` (${company.upiName})` : ""}
                            </div>
                        )}
                        {bank.accountNumber && (
                            <div>
                                {bank.bankName} • A/C {bank.accountNumber} • IFSC {bank.ifsc || "—"}
                            </div>
                        )}
                        {bank.accountName && <div>Name: {bank.accountName}</div>}
                    </div>
                </div>
            )}

            {/* Footer */}
            <div style={{ marginTop: 32, padding: "16px 40px 28px", borderTop: `1px solid ${COLORS.line}`, textAlign: "center" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.primary }}>Thank you for your business!</div>
                <div style={{ fontSize: 10.5, color: COLORS.sub, marginTop: 4 }}>
                    This is a computer-generated invoice and does not require a physical signature.
                </div>
            </div>
        </div>
    );
};

const MetaCell = ({ label, value, last }) => (
    <td
        style={{
            padding: "14px 16px",
            textAlign: "center",
            borderRight: last ? "none" : `1px solid ${COLORS.line}`,
            width: "25%",
        }}
    >
        <div style={{ color: COLORS.sub, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6, textAlign: "center" }}>{label}</div>
        <div style={{ color: COLORS.ink, fontWeight: 600, marginTop: 4, textAlign: "center", fontSize: 12 }}>{value}</div>
    </td>
);

const SectionLabel = ({ children }) => (
    <div style={{ color: COLORS.primary, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, textAlign: "left" }}>
        {children}
    </div>
);

const TotalRow = ({ label, value }) => (
    <tr>
        <td style={{ padding: "4px 16px", fontSize: 13, color: COLORS.sub, textAlign: "left" }}>{label}</td>
        <td style={{ padding: "4px 16px", fontSize: 13, color: COLORS.ink, textAlign: "right", fontWeight: 500 }}>{value}</td>
    </tr>
);

export default InvoiceDocument;
